import React from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheckIcon,
  LockClosedIcon,
  ServerStackIcon,
  DocumentCheckIcon,
  FingerPrintIcon,
  GlobeAltIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const SecurityComplianceSection: React.FC = () => {
  const securityLayers = [
    {
      title: 'Infrastructure Security',
      icon: ServerStackIcon,
      color: 'var(--color-electric-blue)',
      items: [
        'Australian-hosted AWS infrastructure',
        'Multi-region redundancy',
        'DDoS protection',
        '99.9% uptime SLA',
      ],
    },
    {
      title: 'Data Protection',
      icon: LockClosedIcon,
      color: 'var(--color-violet)',
      items: [
        'AES-256 encryption at rest',
        'TLS 1.3 in transit',
        'Field-level encryption for PII',
        'Automated backup & recovery',
      ],
    },
    {
      title: 'Access Control',
      icon: FingerPrintIcon,
      color: 'var(--color-emerald)',
      items: [
        'Role-based access control (RBAC)',
        'Multi-factor authentication',
        'Session management',
        'API key rotation',
      ],
    },
  ];

  const certifications = [
    { name: 'ISO 27001', description: 'Information Security Management', status: 'roadmap' },
    { name: 'SOC 2 Type II', description: 'Security & Availability', status: 'roadmap' },
    { name: 'IRAP', description: 'Information Security Registered Assessors', status: 'planned' },
  ];

  return (
    <div className="space-y-8">
      {/* Security Architecture Visualization */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="card-premium p-8"
      >
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-white mb-2">Defense in Depth Architecture</h3>
          <p className="text-[var(--color-light-gray)]">Multi-layered security protecting every aspect of your data</p>
        </div>

        {/* Visual Security Layers */}
        <div className="relative max-w-3xl mx-auto">
          {/* Outer Layer - Infrastructure */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="relative p-6 rounded-2xl border-2 border-[var(--color-electric-blue)]/30 bg-[var(--color-electric-blue)]/5"
          >
            <div className="absolute -top-3 left-6 px-3 py-1 bg-[var(--color-midnight)] text-[var(--color-electric-blue)] text-sm font-semibold rounded">
              Infrastructure Layer
            </div>

            {/* Middle Layer - Data Protection */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative p-6 rounded-xl border-2 border-[var(--color-violet)]/30 bg-[var(--color-violet)]/5 mt-4"
            >
              <div className="absolute -top-3 left-6 px-3 py-1 bg-[var(--color-midnight)] text-[var(--color-violet)] text-sm font-semibold rounded">
                Encryption Layer
              </div>

              {/* Inner Layer - Access Control */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
                className="relative p-6 rounded-lg border-2 border-[var(--color-emerald)]/30 bg-[var(--color-emerald)]/5 mt-4"
              >
                <div className="absolute -top-3 left-6 px-3 py-1 bg-[var(--color-midnight)] text-[var(--color-emerald)] text-sm font-semibold rounded">
                  Access Control Layer
                </div>

                {/* Core - Student Data */}
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  viewport={{ once: true }}
                  className="flex items-center justify-center p-8 mt-4 rounded-lg bg-gradient-to-br from-[var(--color-amber)]/20 to-[var(--color-amber)]/5 border border-[var(--color-amber)]/30"
                >
                  <div className="text-center">
                    <ShieldCheckIcon className="w-12 h-12 text-[var(--color-amber)] mx-auto mb-2" />
                    <span className="text-white font-bold">Student Data</span>
                    <p className="text-xs text-[var(--color-medium-gray)] mt-1">Protected at Every Layer</p>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>

        {/* Security Layer Details */}
        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {securityLayers.map((layer, index) => (
            <motion.div
              key={layer.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="glass-card p-5"
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${layer.color}20` }}
                >
                  <layer.icon className="w-5 h-5" style={{ color: layer.color }} />
                </div>
                <h4 className="text-white font-semibold">{layer.title}</h4>
              </div>
              <div className="space-y-2">
                {layer.items.map((item) => (
                  <div key={item} className="flex items-start gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-[var(--color-emerald)] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[var(--color-light-gray)]">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Security Roadmap */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        viewport={{ once: true }}
        className="card-premium p-8"
      >
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-white mb-2">Security Certification Roadmap</h3>
          <p className="text-[var(--color-light-gray)]">Our commitment to achieving industry-leading security standards</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {certifications.map((cert, index) => (
            <motion.div
              key={cert.name}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center p-6 bg-white/5 rounded-xl border border-white/10 hover:border-[var(--color-electric-blue)]/50 transition-colors"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--color-electric-blue)]/50 to-[var(--color-violet)]/50 p-0.5 mx-auto mb-4">
                <div className="w-full h-full rounded-full bg-[var(--color-midnight)] flex items-center justify-center">
                  <DocumentCheckIcon className="w-8 h-8 text-[var(--color-electric-blue)]" />
                </div>
              </div>
              <h4 className="text-xl font-bold text-white mb-1">{cert.name}</h4>
              <p className="text-sm text-[var(--color-light-gray)] mb-2">{cert.description}</p>
              <span
                className={`text-xs px-3 py-1 rounded-full ${
                  cert.status === 'planned'
                    ? 'bg-[var(--color-amber)]/20 text-[var(--color-amber)]'
                    : 'bg-[var(--color-electric-blue)]/20 text-[var(--color-electric-blue)]'
                }`}
              >
                {cert.status === 'planned' ? 'Future Planned' : 'On Roadmap'}
              </span>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Data Sovereignty */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        viewport={{ once: true }}
        className="card-premium p-8"
      >
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <GlobeAltIcon className="w-10 h-10 text-[var(--color-electric-blue)]" />
              <h3 className="text-2xl font-bold text-white">Australian Data Sovereignty</h3>
            </div>
            <p className="text-[var(--color-light-gray)] mb-6">
              All student data is stored exclusively within Australian borders. We never transfer or process
              data overseas, ensuring complete compliance with Australian privacy laws and school policies.
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircleIcon className="w-5 h-5 text-[var(--color-emerald)] flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-semibold">Sydney Data Centre</span>
                  <p className="text-sm text-[var(--color-medium-gray)]">Primary infrastructure in AWS Sydney region</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircleIcon className="w-5 h-5 text-[var(--color-emerald)] flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-semibold">Melbourne Failover</span>
                  <p className="text-sm text-[var(--color-medium-gray)]">Redundant backup in Melbourne for disaster recovery</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircleIcon className="w-5 h-5 text-[var(--color-emerald)] flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-semibold">No Offshore Access</span>
                  <p className="text-sm text-[var(--color-medium-gray)]">Data never leaves Australian jurisdiction</p>
                </div>
              </div>
            </div>
          </div>
          <div className="relative flex items-center justify-center">
            <div className="relative w-64 h-48">
              {/* Sydney - Primary */}
              <div className="absolute top-8 right-8">
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  {/* Outer glow ring - animated */}
                  <motion.div
                    className="absolute -inset-4 rounded-full bg-[var(--color-emerald)]/20"
                    animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  <motion.div
                    className="absolute -inset-2 rounded-full bg-[var(--color-emerald)]/30"
                    animate={{ scale: [1, 1.3, 1], opacity: [0.7, 0.2, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
                  />
                  {/* Core dot */}
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[var(--color-emerald)] to-[var(--color-bright-cyan)] shadow-lg shadow-[var(--color-emerald)]/50" />
                </motion.div>
                <p className="text-center text-white font-semibold mt-3">Sydney</p>
                <p className="text-center text-xs text-[var(--color-emerald)]">Primary</p>
              </div>

              {/* Melbourne - Failover */}
              <div className="absolute bottom-8 left-8">
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  {/* Outer glow ring - animated */}
                  <motion.div
                    className="absolute -inset-3 rounded-full bg-[var(--color-violet)]/20"
                    animate={{ scale: [1, 1.4, 1], opacity: [0.5, 0, 0.5] }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
                  />
                  <motion.div
                    className="absolute -inset-1.5 rounded-full bg-[var(--color-violet)]/30"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.7, 0.2, 0.7] }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 0.8 }}
                  />
                  {/* Core dot */}
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[var(--color-violet)] to-[var(--color-electric-blue)] shadow-lg shadow-[var(--color-violet)]/50" />
                </motion.div>
                <p className="text-center text-white font-semibold mt-3">Melbourne</p>
                <p className="text-center text-xs text-[var(--color-violet)]">Failover</p>
              </div>

              </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SecurityComplianceSection;
